//@flow
import { createTheme } from '@material-ui/core/styles'
// Brand tokens — single source of truth (lesofi/gigsdata-brand → @lesofi/phoenix-tokens).
// Material-UI v4 palette + typography for the BAU/crowd app (light + dark).
// Import via the package main entry (not the /mui subpath) so Jest's legacy
// resolver — which ignores the package.json "exports" field — resolves it.
import { mui } from '@lesofi/phoenix-tokens'

const { getPalette, getTypography } = mui

const hexToRgb = (hex: string): string => {
  const n = parseInt(hex.slice(1), 16)
  return `${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}`
}

// Enhanced theme with premium design system — now brand-token driven.
// Supports both light and dark modes (dark is BAU-only per the brand tokens).
export const getTheme = (mode: 'light' | 'dark') => {
  const isDark = mode === 'dark'
  const palette = getPalette(mode)
  const baseType = getTypography('bau') // Outfit display headings + Inter body

  const GOLD = palette.primary.main // gold-500 #CA9A2C
  const GOLD_LIGHT = palette.primary.light // gold-400 #F3C817
  const G = hexToRgb(GOLD) // "202, 154, 44" — gold accent for shadows/borders

  return createTheme({
    palette,
    typography: {
      ...baseType,
      // keep the app's display scale; fontFamily/weights come from the tokens
      h1: { ...baseType.h1, fontSize: '3.5rem', lineHeight: 1.2, letterSpacing: '-0.02em' },
      h2: { ...baseType.h2, fontSize: '2.5rem', lineHeight: 1.3, letterSpacing: '-0.01em' },
      h3: { ...baseType.h3, fontSize: '2rem', lineHeight: 1.4 },
      h4: { ...baseType.h4, fontSize: '1.5rem', lineHeight: 1.4 },
      h5: { ...baseType.h5, fontSize: '1.25rem', lineHeight: 1.5 },
      h6: { ...baseType.h6, fontSize: '1rem', lineHeight: 1.5 },
      body1: { fontSize: '1rem', lineHeight: 1.6, letterSpacing: '0.01em' },
      body2: { fontSize: '0.875rem', lineHeight: 1.6, letterSpacing: '0.01em' },
    },
    shadows: [
      'none',
      '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
      '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
      '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
      '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
      `0 2px 4px rgba(${G}, 0.1), 0 8px 16px rgba(${G}, 0.1)`,
      `0 4px 8px rgba(${G}, 0.15), 0 12px 24px rgba(${G}, 0.15)`,
      `0 8px 16px rgba(${G}, 0.2), 0 16px 32px rgba(${G}, 0.2)`,
      '0 2px 4px rgba(0, 0, 0, 0.1), 0 8px 16px rgba(0, 0, 0, 0.1)',
      '0 4px 8px rgba(0, 0, 0, 0.15), 0 12px 24px rgba(0, 0, 0, 0.15)',
      '0 8px 16px rgba(0, 0, 0, 0.2), 0 16px 32px rgba(0, 0, 0, 0.2)',
      '0 12px 24px rgba(0, 0, 0, 0.15), 0 24px 48px rgba(0, 0, 0, 0.15)',
      '0 16px 32px rgba(0, 0, 0, 0.2), 0 32px 64px rgba(0, 0, 0, 0.2)',
      '0 20px 40px rgba(0, 0, 0, 0.25), 0 40px 80px rgba(0, 0, 0, 0.25)',
      '0 24px 48px rgba(0, 0, 0, 0.3), 0 48px 96px rgba(0, 0, 0, 0.3)',
      '0 28px 56px rgba(0, 0, 0, 0.35), 0 56px 112px rgba(0, 0, 0, 0.35)',
      '0 32px 64px rgba(0, 0, 0, 0.4), 0 64px 128px rgba(0, 0, 0, 0.4)',
      '0 36px 72px rgba(0, 0, 0, 0.45), 0 72px 144px rgba(0, 0, 0, 0.45)',
      '0 40px 80px rgba(0, 0, 0, 0.5), 0 80px 160px rgba(0, 0, 0, 0.5)',
      '0 44px 88px rgba(0, 0, 0, 0.55), 0 88px 176px rgba(0, 0, 0, 0.55)',
      '0 48px 96px rgba(0, 0, 0, 0.6), 0 96px 192px rgba(0, 0, 0, 0.6)',
      '0 52px 104px rgba(0, 0, 0, 0.65), 0 104px 208px rgba(0, 0, 0, 0.65)',
      '0 56px 112px rgba(0, 0, 0, 0.7), 0 112px 224px rgba(0, 0, 0, 0.7)',
      '0 60px 120px rgba(0, 0, 0, 0.75), 0 120px 240px rgba(0, 0, 0, 0.75)',
    ],
    shape: {
      borderRadius: 12, // modern rounded corners
    },
    overrides: {
      MuiButton: {
        root: {
          borderRadius: 12,
          padding: '10px 24px',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: `0 8px 16px rgba(${G}, 0.3)`,
          },
        },
        contained: {
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        },
        containedPrimary: {
          // gold fill; label uses palette.primary.contrastText = charcoal onGold (AA-safe)
          background: `linear-gradient(135deg, ${GOLD} 0%, ${GOLD_LIGHT} 100%)`,
          '&:hover': {
            background: `linear-gradient(135deg, ${GOLD_LIGHT} 0%, ${GOLD} 100%)`,
          },
        },
        // containedSecondary intentionally uses palette.secondary (slate-900) — no custom override
      },
      MuiCard: {
        root: {
          borderRadius: 16,
          background: isDark ? 'rgba(30, 41, 59, 0.7)' : 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(10px)',
          border: isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: isDark
              ? `0 12px 24px rgba(${G}, 0.2)`
              : `0 12px 24px rgba(${G}, 0.3)`,
          },
        },
      },
      MuiPaper: {
        rounded: {
          borderRadius: 12,
        },
        elevation1: {
          boxShadow: isDark
            ? '0 2px 8px rgba(0, 0, 0, 0.1)'
            : '0 2px 8px rgba(0, 0, 0, 0.05)',
        },
      },
      MuiAppBar: {
        colorPrimary: {
          background: isDark
            ? 'rgba(15, 23, 42, 0.85)'
            : 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          borderBottom: isDark
            ? `1px solid rgba(${G}, 0.2)`
            : `1px solid rgba(${G}, 0.3)`,
        },
      },
      MuiTextField: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 12,
            '& fieldset': {
              borderColor: isDark
                ? 'rgba(255, 255, 255, 0.2)'
                : 'rgba(0, 0, 0, 0.2)',
            },
            '&:hover fieldset': {
              borderColor: `rgba(${G}, 0.5)`,
            },
            '&.Mui-focused fieldset': {
              borderColor: GOLD,
            },
          },
        },
      },
    },
  })
}

// Export default dark theme for backward compatibility
export default getTheme('dark')
